import React from 'react';
import {NONE_EXEMPTION, Season} from '@chekin/core';
import SeasonEntity from '../entities/season.entity';
import TaxExemptionsEntity from '../entities/tax-exemptions.entity';
import {useTaxesEnv} from '../env';
import {useGuestTaxesList} from './useGuestTaxesList';
import {useFetchGuestsTaxes, useFetchSeason, useMutateGuestTax} from './common';

function getTaxExemptionsAsOptions(data?: Season['exemptions']) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(exemption => {
    return {
      value: exemption.exemption_type,
      label: exemption.exemption_name,
    };
  });
}

type UseTaxesProps = {
  onError?: (error: Error) => void;
};

function useTaxes({onError}: UseTaxesProps = {}) {
  const {guestGroup} = useTaxesEnv();

  const {
    queryData: {data: highSeason},
    isLoadingSeason,
  } = useFetchSeason();
  const taxExemptions = highSeason?.exemptions;
  const taxExemptionsOptions = React.useMemo(
    () => getTaxExemptionsAsOptions(taxExemptions),
    [taxExemptions],
  );
  const seasonEntity = React.useMemo(() => new SeasonEntity(highSeason), [highSeason]);

  const {
    data: guestsTaxes,
    isLoading: isLoadingGuestsTaxes,
    isFetching: isFetchingGuestsTaxes,
  } = useFetchGuestsTaxes({onError});

  const {
    mutateGuestTax,
    confirmTaxesSetup,
    isLoading: isMutating,
  } = useMutateGuestTax({guestsTaxesId: guestsTaxes?.id, onError});

  const {checkIfGuestExempted, removeExemption, addExemption} = useGuestTaxesList({
    mutate: mutateGuestTax,
    guestsTaxesList: guestsTaxes?.guest_list,
  });

  const displayedGuestsExemptions = React.useMemo(() => {
    if (!guestsTaxes) {
      return [];
    }

    const taxExemptionsEntity = new TaxExemptionsEntity(taxExemptions);

    return guestsTaxes.guest_list
      .filter(({exemption}) => !!exemption && exemption !== NONE_EXEMPTION)
      .map(guestTax => {
        const exemption = taxExemptionsEntity?.findOne(guestTax.exemption);

        const guest = guestGroup?.members?.find(
          singleGuest => singleGuest.id === guestTax.guest_id,
        );

        return {
          full_name: guest?.full_name,
          id: guestTax.id ?? guestTax.guest_id,
          exemption: exemption?.exemption_name,
          isRemovable: !!guestTax?.guest_id,
          isEditable: !guestTax?.guest_id,
        };
      });
  }, [guestsTaxes, taxExemptions, guestGroup?.members]);

  return {
    guestsTaxes,
    season: highSeason,
    taxExemptions,
    taxExemptionsOptions,
    displayedGuestsExemptions,
    seasonEntity,
    confirmTaxesSetup,
    addExemption,
    removeExemption,
    checkIfGuestExempted,
    disabledAddingExemptions: Boolean(
      (guestsTaxes?.guest_list?.length ?? 0) <= displayedGuestsExemptions?.length,
    ),
    isMutating,
    isLoading: isLoadingSeason || isLoadingGuestsTaxes,
    isFetching: isLoadingSeason || isFetchingGuestsTaxes || isMutating,
    hasExemptions: Boolean(taxExemptions?.length),
  };
}

export {useTaxes};
